import React, { useState } from 'react';
import axios from 'axios';
import { FiSend, FiPhone, FiMessageCircle, FiCheck, FiX, FiLoader } from 'react-icons/fi';

const WhatsAppSender = ({ posters, clients, base_url, onClose, onSuccess }) => {
  const [selectedClients, setSelectedClients] = useState([]);
  const [customMessage, setCustomMessage] = useState(`Here's your poster: ${posters.title}`);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredClients = clients.filter(client => 
    client.phone && 
    (client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
     client.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
     client.phone.includes(searchTerm))
  );

  const toggleClient = (clientId) => {
    setSelectedClients(prev =>
      prev.includes(clientId)
        ? prev.filter(id => id !== clientId)
        : [...prev, clientId]
    );
  };

  const selectAll = () => {
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(c => c.id));
    }
  };

  const sendToSelected = async () => {
    if (selectedClients.length === 0) {
      alert('Please select at least one client');
      return;
    }

    setSending(true);
    try {
      const response = await axios.post(`${base_url}/whatsapp/send_bulk_posters.php`, {
        poster_id: posters.id,
        client_ids: selectedClients,
        message: customMessage
      });

      setResults(response.data);
      if (response.data.success) {
        onSuccess?.(response.data);
      }
    } catch (error) {
      console.error('Error sending WhatsApp messages:', error);
      alert('Failed to send messages');
    } finally {
      setSending(false);
    }
  };

  const sendToSingle = async (clientId, phone) => {
    try {
      await axios.post(`${base_url}/whatsapp/send_posters.php`, {
        poster_id: posters.id,
        client_id: clientId,
        phone_number: phone,
        message: customMessage
      });
      alert('Poster sent successfully!');
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      alert('Failed to send message');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <FiMessageCircle className="text-green-600" />
              Send Poster via WhatsApp
            </h3>
            <p className="text-sm text-gray-500 mt-1">{posters.title}</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <FiX size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {/* Left side - Client list */}
          <div className="w-2/3 border-r border-gray-200 flex flex-col">
            <div className="p-4 border-b border-gray-200">
              <input
                type="text"
                placeholder="Search clients by name, company or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <button
                onClick={selectAll}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {selectedClients.length === filteredClients.length ? 'Deselect All' : 'Select All'}
              </button>
              <span className="text-sm text-gray-500">
                {selectedClients.length} of {filteredClients.length} selected
              </span>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
              {filteredClients.map(client => (
                <div
                  key={client.id}
                  className="flex items-center p-3 hover:bg-gray-50 rounded-lg cursor-pointer"
                  onClick={() => toggleClient(client.id)}
                >
                  <input
                    type="checkbox"
                    checked={selectedClients.includes(client.id)}
                    onChange={() => {}}
                    className="mr-3"
                  />
                  <div className="flex-1">
                    <div className="font-medium text-gray-800">{client.name}</div>
                    <div className="text-sm text-gray-500">
                      {client.company && `${client.company} • `}
                      {client.phone}
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      sendToSingle(client.id, client.phone);
                    }}
                    className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                    title="Send to this client only"
                  >
                    <FiSend size={16} />
                  </button>
                </div>
              ))}
              
              {filteredClients.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No clients with phone numbers found
                </div>
              )}
            </div>
          </div>

          {/* Right side - Message preview and send */}
          <div className="w-1/3 flex flex-col">
            <div className="p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom Message
              </label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                rows="4"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-green-500"
                placeholder="Enter your message..."
              />
            </div>

            <div className="p-4 border-t border-gray-200">
              {posters.image_path && (
                <div className="mb-4">
                  <img
                    src={`${base_url}/${posters.image_path}`}
                    alt={posters.title}
                    className="w-full rounded-lg border border-gray-200"
                  />
                </div>
              )}

              <button
                onClick={sendToSelected}
                disabled={sending || selectedClients.length === 0}
                className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white py-3 rounded-lg font-medium hover:from-green-700 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <FiLoader className="animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FiSend />
                    Send to {selectedClients.length} Client{selectedClients.length !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Results */}
        {results && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <h4 className="font-medium text-gray-800 mb-2">Results</h4>
            <div className="flex gap-4 text-sm">
              <span className="text-green-600">✓ Sent: {results.total_sent}</span>
              <span className="text-red-600">✗ Failed: {results.total_failed}</span>
            </div>
            {results.total_failed > 0 && (
              <button
                onClick={() => setResults(null)}
                className="mt-2 text-xs text-blue-600 hover:text-blue-800"
              >
                Clear results
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsAppSender;